import CustomDialog from "../CustomDialog/CustomDialog";
import CustomDialogActions from "../CustomDialog/CustomDialogActions";

import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Alert, Snackbar } from "@mui/material";

import { useSessionIdContext } from "@/context/SessionIdContext";

import LoadConfigurationFormContent, {
  ResultsTableRow,
} from "./LoadConfigurationFormContent";
import { ResultsTableDialog } from "./ResultsTableDialog";

export default function LoadConfigurationDialog({
  disabled,
  renderTrigger,
}: {
  disabled?: boolean;
  renderTrigger: (props: {
    onClick: (e: React.MouseEvent<unknown>) => void;
  }) => ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const submitFormRef = useRef<HTMLFormElement>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [tableData, setTableData] = useState<ResultsTableRow[] | null>(null);
  const [formData, setFormData] = useState<Record<string, unknown> | null>(
    null,
  );
  const [selectedRow, setSelectedRow] = useState<
    Partial<ResultsTableRow> | undefined
  >(undefined);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const { setLoadedSessionIdParam } = useSessionIdContext();

  const handleClose = (): void => {
    setTimeout(() => {
      setTableData(null);
      setSelectedRow(undefined);
      setStep(1);
      setFormData(null);
    }, 200);
  };

  const handleShowResults = (
    data: ResultsTableRow[],
    submittedFormData?: Record<string, unknown>,
  ): void => {
    setTableData(
      data.filter((configuration) => configuration.errorStateEmpty === ""),
    );
    setStep(2);
    setSelectedRow(undefined);
    if (submittedFormData) setFormData(submittedFormData);
  };

  const handleBackToForm = (): void => {
    setStep(1);
  };

  const handleOpen = (): void => {
    if (selectedRow?.errorBlocked) {
      setSnackbarOpen(true);
      return;
    }
    setLoadedSessionIdParam(selectedRow?.id);
  };

  return (
    <>
      <CustomDialog
        disabled={disabled}
        renderTrigger={renderTrigger}
        dialog={{
          dialogProps: {
            maxWidth: step === 1 ? "md" : "xl",
          },
          onClose: handleClose,
          title:
            step === 1
              ? t("load_configuration_dialog.title")
              : t("load_configuration_dialog.saved_records"),
          actions: ({ closeDialog }) =>
            step === 1 ? (
              <CustomDialogActions
                variant="step1"
                textCancel={t("load_configuration_dialog.cancel")}
                textConfirm={t("load_configuration_dialog.search")}
                onCancel={() => {
                  closeDialog();

                  handleClose();
                }}
                onConfirm={() => submitFormRef?.current?.requestSubmit()}
              />
            ) : (
              <CustomDialogActions
                variant="step2"
                onBack={handleBackToForm}
                textBack={t("load_configuration_dialog.back")}
                onOpen={handleOpen}
                textOpen={t("load_configuration_dialog.open")}
                openDisabled={!selectedRow?.id}
                onCancel={() => {
                  closeDialog();
                  handleClose();
                }}
                textCancel={t("load_configuration_dialog.cancel")}
              />
            ),
          content:
            step === 1 ? (
              <LoadConfigurationFormContent
                submitFormRef={submitFormRef}
                onShowResults={handleShowResults}
                formData={formData}
                setFormData={setFormData}
              />
            ) : (
              <ResultsTableDialog
                data={tableData}
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
              />
            ),
          children: (
            <Snackbar
              open={snackbarOpen}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              autoHideDuration={6000}
              onClose={() => setSnackbarOpen(false)}
            >
              <Alert
                onClose={() => setSnackbarOpen(false)}
                severity="warning"
                variant="filled"
              >
                {t("error.ERROR_ID_BLOCKED")}
              </Alert>
            </Snackbar>
          ),
        }}
      />
    </>
  );
}
